import { useCallback, useRef } from 'react'

type UseGuardedModalDismissArgs = {
  /** When true, dismiss is blocked unless `markConfirmComplete` was called. */
  shouldBlock: boolean
  onClose: () => void
}

/**
 * Encapsulates the dismiss pattern for modals that block close during async work
 * (e.g. form submission, exports) while still letting Radix run teardown before
 * the parent unmounts.
 */
export const useGuardedModalDismiss = ({ shouldBlock, onClose }: UseGuardedModalDismissArgs) => {
  const dismissModalRef = useRef<() => void>(() => {})
  const allowCloseAfterConfirmRef = useRef(false)

  const handleCloseIntent = useCallback(() => {
    if (shouldBlock && !allowCloseAfterConfirmRef.current) {
      return
    }
    dismissModalRef.current()
  }, [shouldBlock])

  const bindDismiss = useCallback((dismiss: () => void) => {
    dismissModalRef.current = dismiss
  }, [])

  const handleModalClosed = useCallback(() => {
    allowCloseAfterConfirmRef.current = false
    onClose()
  }, [onClose])

  /** Call after async `onConfirm` resolves so `Modal.Footer` can close while `shouldBlock` is still true. */
  const markConfirmComplete = useCallback(() => {
    allowCloseAfterConfirmRef.current = true
  }, [])

  /** Programmatic dismiss, e.g. from an unsaved-changes discard flow. */
  const dismiss = useCallback(() => {
    dismissModalRef.current()
  }, [])

  return {
    onCloseIntent: handleCloseIntent,
    onBindDismiss: bindDismiss,
    onModalClosed: handleModalClosed,
    markConfirmComplete,
    dismiss,
  }
}
